import _Promise from "@babel/runtime-corejs2/core-js/promise";
import { superpathjoin } from 'superpathjoin';
import { createHistory } from 'history';
import { useRouterHistory, createMemoryHistory } from 'react-router';
import { logging } from 'gandi.redux-logger';
import { localeSelector } from '@gandi/translate';
import { fetchCurrentSharingAndSetPersonal, setCurrentId } from '../modules/organizations/actions';
import { selectCurrentId, selectPersonalId } from '../modules/organizations/selectors';

/**
 * Configure the router for the server to handle the id of the sharing space.
 */
export var serverRouterConfig = function serverRouterConfig(endpoint) {
  var hasPublicRoutes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var hasSubsetOrgSupport = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  var persistSharingId = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : true;
  return function (store, req, res) {
    var logger = logging(store.dispatch, '@gandi/redux-sharing/router');

    var createRouterOptions = function createRouterOptions(basename) {
      logger.info("Creating Router Options for basename: ".concat(basename, " ..."));
      var history = createMemoryHistory({
        basename: basename
      });
      var location = history.createLocation(req.url.replace(basename, '') || '/');
      return {
        history: history,
        location: location
      };
    };

    var redirectWithCookieSharing = function redirectWithCookieSharing(locale, cookieSharingId, originalPathWithoutBasename) {
      if (cookieSharingId) {
        var redirectUrl = superpathjoin(endpoint, locale, cookieSharingId, originalPathWithoutBasename, true);
        logger.info("No sharing id in url but in cookie sharing_id. Redirecting to ".concat(redirectUrl));
        res.redirect(302, redirectUrl);
        return _Promise.reject(new Error('No sharing id in url but in cookie sharing_id'));
      }

      return _Promise.reject(new Error('Internal error: missing cookieSharingId'));
    }; // eslint-disable-next-line prefer-regex-literals


    var uuidRE = new RegExp(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}$/i); // Successively extract the basename out of the path...

    var originalPathWithoutBasename = req.url.replace(endpoint, '').replace(/^\//, '');
    var state = store.getState();
    var locale = hasPublicRoutes ? localeSelector(state) : '';

    if (hasPublicRoutes) {
      if (!locale) {
        return _Promise.reject(new Error('Missing locale'));
      }

      var urlLocale = originalPathWithoutBasename.split('/')[0].split(/[?#]/)[0];

      if (!urlLocale || urlLocale !== locale) {
        // If url doesn't contain locale, redirect to current locale
        var redirectUrl = superpathjoin(endpoint, locale, originalPathWithoutBasename, true);
        logger.info("No locale in url. Redirecting to ".concat(redirectUrl)); // max-age=600: cache for 10minutes in the browser
        // s-maxage=3600: cache for 1hour in proxies
        // stale-while-revalidate=10800: serve cached response from proxy,
        //    while proxy retrieves up-to-date version
        // stale-if-error=10800: serve cached response from proxy,
        //    while backend is broken.
        // Use `X-Cache-Control-After-Gzip` to set `Cache-Control` after gzip.

        res.header('X-Cache-Control-After-Gzip', 'max-age=600, s-maxage=3600, stale-while-revalidate=10800, stale-if-error=10800, public, no-transform');
        res.append('Vary', 'Accept-Language');
        res.redirect(302, redirectUrl);
        return _Promise.reject(new Error('No locale in url'));
      }

      originalPathWithoutBasename = originalPathWithoutBasename.replace(urlLocale, '').replace(/^\//, '');

      if (!req.tokeninfo) {
        // Got an url for when you were authenticated?
        // Let's redirect back to the "public" url.
        var _sharingId = originalPathWithoutBasename.split('/')[0].split(/[?#]/)[0];
        originalPathWithoutBasename = originalPathWithoutBasename.replace(_sharingId, '').replace(/^\//, '');

        if (_sharingId.match(uuidRE)) {
          var _redirectUrl = superpathjoin(endpoint, locale, originalPathWithoutBasename, true);

          logger.info('Not authenticated. Found extraneous sharing id in url. ' + "Redirecting to ".concat(_redirectUrl));
          res.redirect(302, _redirectUrl);
          return _Promise.reject(new Error('Found extraneous sharing id in url'));
        }

        var basename = superpathjoin(endpoint, locale, true);
        return _Promise.resolve(createRouterOptions(basename));
      }
    }

    var sharingId = originalPathWithoutBasename.split('/')[0].split(/[?#]/)[0];
    logger.info("Extract sharingId `".concat(sharingId, "` from url `").concat(req.url, "`")); // Nothing left in the path? No sharing id and no path, just the root basename.

    if (!sharingId) {
      if (persistSharingId) {
        var cookieSharingId = req.cookies.get('sharing_id');

        if (cookieSharingId) {
          return redirectWithCookieSharing(locale, cookieSharingId, originalPathWithoutBasename);
        }
      }

      if (req.tokeninfo) {
        // If url doesn't contain sharingId, redirect to personal organization
        var _redirectUrl2 = superpathjoin(endpoint, locale, req.tokeninfo.user_id, true);

        logger.info("No sharing id either url or cookie. Redirecting to ".concat(_redirectUrl2));
        res.redirect(302, _redirectUrl2);
      }

      return _Promise.reject(new Error('No sharing id in url'));
    } // Is it an UUID?


    if (!sharingId.match(uuidRE)) {
      if (persistSharingId) {
        var _cookieSharingId = req.cookies.get('sharing_id');

        if (_cookieSharingId) {
          return redirectWithCookieSharing(locale, _cookieSharingId, originalPathWithoutBasename);
        }
      }

      var _redirectUrl3 = superpathjoin(endpoint, locale, req.tokeninfo.user_id, originalPathWithoutBasename, true); // This sharingId does not exist.
      // Redirect to the original URL, augmented with the userId (personal organization)


      logger.info("No sharing id in url. Redirecting to ".concat(_redirectUrl3));
      res.redirect(302, _redirectUrl3);
      return _Promise.reject(new Error('No sharing id in url'));
    } // Sharing id exists in the path, let's extract the real path.


    originalPathWithoutBasename = originalPathWithoutBasename.replace(sharingId, '').replace(/^\//, ''); // Is it an authorized sharing id?

    return store.dispatch(fetchCurrentSharingAndSetPersonal(sharingId, req, hasSubsetOrgSupport)).then(function () {
      if (persistSharingId) {
        res.cookie('sharing_id', sharingId, {
          httpOnly: true,
          secure: true,
          maxAge: 1 * 365 * 24 * 60 * 60 * 1000,
          // ~1 year (maximum legal time: 13 months)
          path: endpoint
        });
      }

      var basename = superpathjoin(endpoint, locale, sharingId, true);
      return createRouterOptions(basename);
    }).catch(function (error) {
      // Try to handle edge-cases.
      var updatedState = store.getState();
      var personalId = selectPersonalId(updatedState);

      if (!personalId || sharingId === personalId) {
        var currentId = selectCurrentId(updatedState);

        if (!currentId) {
          logger.warn('Warning: unexpected case happened. As a last resort, ' + "try setting the sharing_id ".concat(sharingId, " as the current id, ") + "as it's the user's personal id. It means the user exists in oauth " + 'but not in the organizations database. But we will let the app ' + 'handle the error management instead of ending with infinite redirects.');
          store.dispatch(setCurrentId(sharingId));
        }

        var _basename = superpathjoin(endpoint, locale, sharingId, true);

        return createRouterOptions(_basename);
      } // Invalid sharing_id, let's redirect to the app's basename without a sharing_id.


      var redirectUrl = superpathjoin(endpoint, locale, req.tokeninfo.user_id, originalPathWithoutBasename, true);
      res.redirect(302, redirectUrl);
      var sharingIdError = error;
      sharingIdError.message = "Invalid sharing id: ".concat(error.message);

      if (!!error.statusCode && error.statusCode !== 403) {
        logger.error({
          err: sharingIdError
        }, "Invalid sharing id: ".concat(error.message));
      }

      return _Promise.reject(sharingIdError);
    });
  };
};
/**
 * Configure the router for the client to handle the id of the sharing space.
 */

/* global window */

export var clientRouterConfig = function clientRouterConfig(endpoint) {
  var hasPublicRoutes = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var pivoineEndpoint = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : '/api/v5';
  return function (store) {
    if (typeof window !== 'undefined') {
      var pathname = window.location.pathname;

      if (pathname.lastIndexOf(pivoineEndpoint, 0) === 0) {
        return _Promise.reject(new Error('Error page'));
      }
    }

    var state = store.getState();
    var locale = hasPublicRoutes ? localeSelector(state) : '';
    var currentId = selectCurrentId(state);

    if (hasPublicRoutes) {
      if (!locale) {
        return _Promise.reject(new Error('Missing locale'));
      }
    } else if (!currentId) {
      return _Promise.reject(new Error('Missing sharing id'));
    }

    var basename = superpathjoin(endpoint, locale, currentId, true);
    var history = useRouterHistory(createHistory)({
      basename: basename
    });
    var routerOptions = {
      history: history
    };
    return _Promise.resolve(routerOptions);
  };
};
var routerConfigs = {
  server: serverRouterConfig,
  client: clientRouterConfig
};
export default routerConfigs;